// src/components/sync/useSyncPanelModel.js
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { deriveSyncPanelModel } from '../../utils/syncPanelModel'

/**
 * Relógio do "há X min": Date.now() ao montar, quando `dep` muda e a cada `tickMs` (0 = sem tick).
 * A barra e o painel usam o MESMO relógio (QA HML U14: "há 6 min" na barra e "há 7 min" no painel
 * no mesmo instante — a barra media pelo `readAt` da última leitura, o painel pelo Date.now()).
 */
export function useNow(tickMs = 0, dep) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    setNow(Date.now())
    if (!tickMs) return undefined
    const id = setInterval(() => setNow(Date.now()), tickMs)
    return () => clearInterval(id)
  }, [tickMs, dep])
  return now
}

/**
 * tickMs > 0 re-renderiza a cada tickMs (tempo decorrido/"há X min"); 0 = só quando os dados mudam.
 * `now` vindo de fora (o relógio da barra) substitui o relógio próprio.
 */
export function useSyncPanelModel(
  snapshot,
  { tickMs = 0, now: sharedNow } = {},
) {
  const sync = useSelector((s) => s.sync)
  const ownNow = useNow(sharedNow == null ? tickMs : 0, snapshot)
  const now = sharedNow ?? ownNow

  return useMemo(
    () =>
      deriveSyncPanelModel({
        now,
        online: snapshot?.online ?? true,
        summary: snapshot?.summary ?? null,
        lastPushOkAt: snapshot?.lastPushOkAt ?? null,
        lastPullAt: snapshot?.lastPullAt ?? null,
        currentSync: sync.currentSync,
        operation: sync.operation,
        progress: sync.progress,
        lastPull: sync.lastPull,
        notice: sync.notice,
      }),
    [now, snapshot, sync],
  )
}
